import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, type ErrorComponentProps } from "@tanstack/react-router";
import { z } from "zod";
import { ListPagination, paginate } from "@/components/list-pagination";
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from "@/components/ui/empty";
import { Item, ItemActions, ItemContent, ItemDescription, ItemTitle } from "@/components/ui/item";
import { InviteMemberDialog } from "@/features/reseller/invite-member/dialog";
import { invitationsQuery, membersQuery } from "@/features/reseller/queries";
import { InvitationList, TeamList } from "@/features/reseller/team-list";
import { invitableBy } from "@/features/reseller/team-roles";
import { ApiError } from "@/lib/api-client";
import { m } from "@/lib/paraglide/messages.js";

const PAGE_SIZE = 10;

// ?page= is route state (guide, rule 16): a link to page 2 opens page 2. Anything else reads as 1.
const MembersSearch = z.object({
  page: z.coerce.number().int().min(1).optional().catch(undefined),
});

// /settings/members: the team (sidebar "Team"), after the staff page. Every member reads it; the
// invite button shows only for a role that may hand one out. The button is a courtesy, the API's
// guard and role check are the gate (guide, rule 14).
export const Route = createFileRoute("/_authenticated/settings/members")({
  validateSearch: MembersSearch,
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(membersQuery()),
      context.queryClient.ensureQueryData(invitationsQuery()),
    ]),
  staticData: { crumb: m.team_nav },
  component: MembersPage,
  errorComponent: MembersError,
});

// The way the API keys page says it: a role the API refuses gets that as a page, anything else
// the shared "try again".
function MembersError({ error }: ErrorComponentProps) {
  const forbidden = error instanceof ApiError && error.status === 403;
  return (
    <Empty>
      <EmptyHeader>
        <EmptyTitle>{forbidden ? m.team_no_access_title() : m.common_error_title()}</EmptyTitle>
        <EmptyDescription>{forbidden ? m.team_no_access_description() : m.common_error_description()}</EmptyDescription>
      </EmptyHeader>
    </Empty>
  );
}

function MembersPage() {
  const { user } = Route.useRouteContext();
  const { page: asked = 1 } = Route.useSearch();
  const { data: members } = useSuspenseQuery(membersQuery());
  const { data: invitations } = useSuspenseQuery(invitationsQuery());
  // The signed-in person's role is their row in the team: the session names them, the team their role.
  const role = members.find((member) => member.email === user.email)?.role;
  // The API sends the whole team (a handful), so the page is cut here.
  const { page, pages, items, range } = paginate(members, asked, PAGE_SIZE);
  return (
    <>
      <Item>
        <ItemContent>
          <ItemTitle>{m.team_nav()}</ItemTitle>
          <ItemDescription>{m.team_page_description()}</ItemDescription>
        </ItemContent>
        {role && invitableBy(role).length > 0 && (
          <ItemActions>
            <InviteMemberDialog actorRole={role} />
          </ItemActions>
        )}
      </Item>
      <TeamList
        members={items}
        currentEmail={user.email}
        actorRole={role}
        caption={range}
        pagination={<ListPagination page={page} pages={pages} />}
      />
      <InvitationList invitations={invitations} actorRole={role} />
    </>
  );
}
